/*
  <product-bundle> — gedrag van snippets/product-bundle.liquid ("1 stuk" of "koop N-1, krijg 1 gratis").

  De snippet levert de gegevens als JSON in het element zelf; dit bestand kent geen enkel
  product. Toevoegen aan de winkelwagen gaat via de gewone koopknop van het thema: het element
  antwoordt op `product-form:collect-lines` (assets/product-form-lines.js) met de gekozen
  stukken. Zonder productformulier gebruikt het een eigen knop.

  Regel-eigenschappen: namen die met "_" beginnen zijn voor de klant verborgen in winkelwagen en
  checkout, maar wel zichtbaar in de admin. De gekozen opties zitten al in de variant en worden
  dus niet nog eens als eigenschap meegestuurd.
*/

if (!customElements.get('product-bundle')) {
  const KEY_SEP = String.fromCharCode(31); // scheidt optiewaarden in de opzoeksleutel; komt niet in een waarde voor

  function h(tag, props = {}, ...children) {
    const el = document.createElement(tag);
    Object.entries(props).forEach(([name, value]) => {
      if (value === false || value == null) return;
      if (name === 'class') el.className = value;
      else if (name === 'text') el.textContent = value;
      else if (name.startsWith('on')) el.addEventListener(name.slice(2), value);
      else el.setAttribute(name, value === true ? '' : value);
    });
    children.flat().forEach((child) => child && el.append(child));
    return el;
  }

  const fill = (template, values) =>
    Object.entries(values).reduce((text, [token, value]) => text.split(`%%${token}%%`).join(value), template);

  class ProductBundle extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const json = this.querySelector('script[type="application/json"]');
      if (!json) return;
      this.ready = true;

      this.data = JSON.parse(json.textContent);
      this.i18n = this.data.i18n;
      this.size = this.data.size;
      this.hasPersonalize = this.data.personalizePosition >= 0;
      this.hasImages = this.data.variants.some((variant) => variant.image);
      this.variants = new Map(this.data.variants.map((variant) => [variant.options.join(KEY_SEP), variant]));

      // Tweekleurige swatch uit het variant-metafield; alleen zinvol bij één optie.
      this.overrideByValue = {};
      if (this.data.dims.length === 1) {
        this.data.variants.forEach((variant) => {
          if (variant.swatch && !this.overrideByValue[variant.options[0]]) {
            this.overrideByValue[variant.options[0]] = variant.swatch;
          }
        });
      }

      const first = this.data.variants.find((variant) => variant.available) || this.data.variants[0];
      this.defaultSelection = this.data.dims.map((dim, index) => {
        const optionIndex = index >= this.data.personalizePosition && this.hasPersonalize ? index + 1 : index;
        return first.options[optionIndex] || dim.values[0].name;
      });

      this.state = {
        mode: 'bundle',
        single: this.newItem(),
        bundle: Array.from({ length: this.size }, () => this.newItem()),
      };

      this.modeEls = this.querySelectorAll('.pb-mode');
      this.singleRows = this.querySelector('[data-role="single-rows"]');
      this.bundleRows = this.querySelector('[data-role="bundle-rows"]');
      this.status = this.querySelector('[data-role="status"]');
      this.addButton = this.querySelector('[data-role="add-to-cart"]');
      this.byRole = (name) => this.querySelector(`[data-role="${name}"]`);

      this.querySelectorAll('.pb-mode__header').forEach((header) => {
        header.addEventListener('click', () => {
          this.state.mode = header.closest('.pb-mode').dataset.mode;
          this.render();
        });
      });

      this.addButton.addEventListener('click', () => this.addDirect());

      document.addEventListener('product-form:collect-lines', (event) => this.collectLines(event.detail));

      // Het productformulier staat meestal onder dit element, dus pas zoeken als de pagina geladen is.
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => this.hideOwnButton());
      else this.hideOwnButton();

      this.render();
    }

    /* --- Gegevens ---------------------------------------------------------- */

    newItem() {
      return {
        selection: [...this.defaultSelection],
        personalize: false,
        included: Boolean(this.data.includedLabel),
        text: '',
      };
    }

    optionsFor(item, personalize = item.personalize) {
      const options = [];
      let dim = 0;
      for (let position = 0; position < this.data.optionCount; position++) {
        if (position === this.data.personalizePosition) options.push(this.data.personalizeValues[personalize ? 1 : 0]);
        else options.push(item.selection[dim++]);
      }
      return options;
    }

    variantFor(item, personalize) {
      return this.variants.get(this.optionsFor(item, personalize).join(KEY_SEP));
    }

    priceOf(item) {
      const variant = this.variantFor(item);
      return variant ? variant.price : 0;
    }

    // Index van het goedkoopste (dus gratis) stuk in de bundel — zelfde regel als Shopify's
    // automatische "Buy X get Y"-korting. Gedeeld tussen render() (welke rij toont GRATIS) en
    // lineFor() (welke cart-regel de "gratis inpak"-eigenschap krijgt), zodat die twee nooit
    // uit elkaar kunnen lopen.
    freeIndexOf(bundleArray) {
      const prices = bundleArray.map((item) => this.priceOf(item));
      return prices.reduce((cheapest, price, i) => (price < prices[cheapest] ? i : cheapest), 0);
    }

    // Bestaat deze combinatie én is hij leverbaar, met de overige keuzes van dit stuk?
    isOffered(item, dimIndex, valueName) {
      const probe = { ...item, selection: item.selection.map((value, i) => (i === dimIndex ? valueName : value)) };
      const variant = this.variantFor(probe);
      return Boolean(variant && variant.available);
    }

    formatMoney(cents, compact) {
      return window.zawajMoney.format(cents, { locale: this.data.locale, currency: this.data.currency, compact });
    }

    personalizeFee(item) {
      const standard = this.variantFor(item, false);
      const personalized = this.variantFor(item, true);
      return standard && personalized ? personalized.price - standard.price : 0;
    }

    /* --- Opbouw ------------------------------------------------------------ */

    swatch(dimIndex, value, item) {
      const active = item.selection[dimIndex] === value.name;
      const offered = this.isOffered(item, dimIndex, value.name);
      const twoTone = this.overrideByValue[value.name];
      const label = offered ? value.label : `${value.label} — ${this.i18n.unavailable}`;
      const props = {
        type: 'button',
        'aria-label': label,
        'aria-pressed': String(active),
        title: label,
        disabled: !offered,
        'data-key': `${dimIndex}:${value.name}`,
        onclick: () => {
          item.selection[dimIndex] = value.name;
          this.render(`${dimIndex}:${value.name}`);
        },
      };

      const color = twoTone ? `linear-gradient(90deg, ${twoTone[0]} 50%, ${twoTone[1]} 50%)` : value.color;
      const button = color
        ? h('button', { ...props, class: 'pb-swatch' })
        : h('button', { ...props, class: 'pb-pill', text: value.label });
      if (color) button.style.background = color;
      if (active) button.classList.add('is-active');
      return button;
    }

    row(item, { index = null, free = false } = {}) {
      const variant = this.variantFor(item);
      const inBundle = index !== null;

      // Geen zichtbare "Stuk N" meer (bespaart een regel per stuk); voor schermlezers blijft de
      // rij wel gegroepeerd en benoemd, zodat het onderscheid tussen stukken niet verdwijnt.
      const rowProps = { class: `pb-row${inBundle ? '' : ' pb-row--single'}` };
      if (inBundle) {
        rowProps.role = 'group';
        rowProps['aria-label'] = fill(this.i18n.pieceLabel, { NUMBER: index + 1 });
      }

      const priceCol = h(
        'div',
        { class: 'pb-row__price-col' },
        h('span', { class: 'pb-row__price', text: variant ? this.formatMoney(variant.price) : '' }),
        free && h('span', { class: 'pb-row__free', text: this.i18n.freeBadge })
      );

      const dims = this.data.dims.map((dim, dimIndex) => {
        const current = dim.values.find((value) => value.name === item.selection[dimIndex]);
        return h(
          'div',
          { class: 'pb-dim' },
          h(
            'div',
            { class: 'pb-dim__head' },
            this.data.dims.length > 1 && h('span', { class: 'pb-dim__label', text: dim.name }),
            h('span', { class: 'pb-dim__value', text: current ? current.label : '' })
          ),
          h('div', { class: 'pb-dim__options' }, dim.values.map((value) => this.swatch(dimIndex, value, item)))
        );
      });

      const thumb =
        this.hasImages &&
        h('img', { class: 'pb-row__thumb', alt: '', src: (variant && variant.image) || '', loading: 'lazy' });

      return h(
        'div',
        rowProps,
        h('div', { class: 'pb-row__body' }, thumb, h('div', { class: 'pb-row__dims' }, dims), priceCol),
        this.hasPersonalize && this.personalization(item)
      );
    }

    chip(label, active, key, onToggle, extra) {
      return h(
        'button',
        { type: 'button', class: `pb-chip${active ? ' is-active' : ''}`, 'aria-pressed': String(active), 'data-key': key, onclick: onToggle },
        `${label} `,
        extra
      );
    }

    personalization(item) {
      const chips = h('div', { class: 'pb-row__chips' });
      const includedLabel = this.data.includedLabel;

      if (includedLabel) {
        chips.append(
          this.chip(includedLabel, item.included, 'included', () => {
            item.included = !item.included;
            this.render('included');
          })
        );
      }

      const fee = this.personalizeFee(item);
      chips.append(
        this.chip(
          this.i18n.personalizeLabel,
          item.personalize,
          'personalize',
          () => {
            item.personalize = !item.personalize;
            this.render('personalize');
          },
          fee ? h('span', { class: 'pb-chip__fee', text: `+${this.formatMoney(fee, true)}` }) : null
        )
      );

      const wrap = h('div', {}, chips);
      if (item.personalize) {
        if (includedLabel && item.included) {
          wrap.append(h('div', { class: 'pb-row__note', text: fill(this.i18n.includedPlusCustomNote, { INCLUDED: includedLabel }) }));
        }
        wrap.append(
          h('input', {
            type: 'text',
            class: 'pb-row__input',
            placeholder: this.i18n.textPlaceholder,
            'aria-label': this.i18n.personalizationTextLabel,
            maxlength: 40,
            value: item.text,
            oninput: (event) => {
              item.text = event.target.value;
              event.target.classList.remove('is-invalid');
            },
          })
        );
      }
      return wrap;
    }

    render(focusKey) {
      const active = document.activeElement;
      const restoreKey = focusKey || (this.contains(active) && active.dataset && active.dataset.key);
      const restoreRow = restoreKey && this.contains(active) ? [...this.querySelectorAll('.pb-row')].indexOf(active.closest('.pb-row')) : -1;

      this.modeEls.forEach((mode) => {
        const isActive = mode.dataset.mode === this.state.mode;
        mode.classList.toggle('is-active', isActive);
        mode.querySelector('.pb-mode__header').setAttribute('aria-expanded', String(isActive));
      });

      this.singleRows.replaceChildren(this.row(this.state.single));
      // Geen prijs meer in de header van "1 stuk" (staat al op de rij zelf); alleen de variant nog
      // nodig om de (verborgen) thema-kiezer synchroon te houden.
      const singleVariant = this.variantFor(this.state.single);
      if (this.state.mode === 'single') this.syncThemePicker(singleVariant);

      const prices = this.state.bundle.map((item) => this.priceOf(item));
      const freeIndex = this.freeIndexOf(this.state.bundle);
      this.bundleRows.replaceChildren(
        ...this.state.bundle.map((item, index) => this.row(item, { index, free: index === freeIndex }))
      );

      const sum = prices.reduce((total, price) => total + price, 0);
      const payable = sum - prices[freeIndex];
      // De grote stuksprijs, de doorgestreepte prijs en de "Bespaar X% (+ gratis verzending)"-badge
      // staan allemaal vast in de markup (snippets/product-bundle.liquid) — dat is het anker-bedrag
      // (CRO), dat mag niet meebewegen met de gekozen kleuren/personalisatie. Alleen het totaalbedrag,
      // klein eronder, volgt de echte keuzes.
      this.byRole('bundle-header-total').textContent = fill(this.i18n.bundleTotalCaption, {
        TOTAL: this.formatMoney(payable),
      });
      const perkValue = this.byRole('free-perk-value');
      if (perkValue) perkValue.textContent = this.data.freePerkValue ? this.formatMoney(this.data.freePerkValue) : '';
      // "Totaal" staat niet meer in de markup (de prijs staat al in de mode-box); guard voor een thema
      // dat 'm ooit weer toevoegt.
      const total = this.byRole('total');
      if (total) {
        total.textContent = this.formatMoney(this.state.mode === 'single' ? this.priceOf(this.state.single) : payable);
      }

      if (restoreKey) {
        const rows = this.querySelectorAll('.pb-row');
        const scope = restoreRow >= 0 && rows[restoreRow] ? rows[restoreRow] : this;
        const target = [...scope.querySelectorAll('[data-key]')].find((el) => el.dataset.key === restoreKey && !el.disabled);
        if (target) target.focus();
      }
    }

    // Houdt de (verborgen) standaardkiezer van het thema gelijk met "1 stuk".
    syncThemePicker(variant) {
      const picker = document.querySelector('variant-selects');
      if (!variant || !picker) return;
      variant.options.forEach((value) => {
        const input = [...picker.querySelectorAll('input[type="radio"]')].find((el) => el.value === value);
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    /* --- Winkelwagen ------------------------------------------------------- */

    showStatus(message, isError) {
      this.status.hidden = false;
      this.status.classList.toggle('is-error', isError);
      this.status.setAttribute('role', isError ? 'alert' : 'status');
      this.status.textContent = message;
    }

    // Eén regel voor de winkelwagen/admin die alles over de tekstkeuze samenvat — geen aparte
    // "Personalisatietekst"-regel meer ernaast, dat was twee keer hetzelfde zeggen. Lege string =
    // niets gekozen; lineFor() laat de regel dan helemaal weg (de standaard "Personalisatie:
    // Standaard" van de variant zelf zegt al genoeg, dat hoeft niet dubbelop).
    textState(item) {
      const includedLabel = this.data.includedLabel;
      const included = includedLabel && item.included;
      const text = item.personalize ? item.text.trim() : '';
      if (included && text) return fill(this.i18n.textIncludedPlusCustomText, { INCLUDED: includedLabel, TEXT: text });
      if (included) return includedLabel;
      // Eigen tekst zónder de standaardtekst: nooit de kale tekst tonen, dat oogt als een
      // onbenoemd veld ("sadas") — altijd met een label ervoor.
      if (text) return fill(this.i18n.textCustomOnlyText, { TEXT: text });
      return '';
    }

    validate() {
      if (!this.hasPersonalize) return true;
      const single = this.state.mode === 'single';
      const items = single ? [this.state.single] : this.state.bundle;
      const inputs = [...(single ? this.singleRows : this.bundleRows).querySelectorAll('.pb-row__input')];

      let inputIndex = 0;
      let firstInvalid = null;
      items.forEach((item) => {
        if (!item.personalize) return;
        const input = inputs[inputIndex++];
        if (item.text.trim() || !input) return;
        input.classList.add('is-invalid');
        firstInvalid = firstInvalid || input;
      });
      if (!firstInvalid) return true;

      this.showStatus(this.i18n.textRequired, true);
      firstInvalid.focus();
      firstInvalid.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return false;
    }

    lineFor(item, index, bundleId, freeIndex) {
      const variant = this.variantFor(item);
      if (!variant) return null;

      const properties = {};
      if (this.hasPersonalize || this.data.includedLabel) {
        const designValue = this.textState(item);
        if (designValue) properties[this.i18n.textLabel] = designValue;
      }
      if (index !== null) {
        properties._bundle_id = bundleId;
        properties._bundle_position = `${index + 1}/${this.size}`;
        // Op de regel van het daadwerkelijk gratis stuk (zelfde stuk als de GRATIS-badge op de
        // pagina) — niet zomaar de eerst gekozen regel, anders staat de "gratis inpak"-melding
        // op een betaald (bijv. gepersonaliseerd) stuk in plaats van het gratis stuk.
        if (index === freeIndex && this.data.showFreePerk) properties[this.i18n.giftWrapLabel] = this.i18n.giftWrapValue;
      }
      return { id: variant.id, quantity: 1, properties };
    }

    currentLines() {
      if (this.state.mode === 'single') return [this.lineFor(this.state.single, null)].filter(Boolean);
      const bundleId = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      const freeIndex = this.freeIndexOf(this.state.bundle);
      return this.state.bundle.map((item, index) => this.lineFor(item, index, bundleId, freeIndex)).filter(Boolean);
    }

    get pageSection() {
      return this.closest('[id^="shopify-section"]') || document;
    }

    // Met het productformulier van het thema blijft de gewone koopknop de enige knop.
    hideOwnButton() {
      if (this.pageSection.querySelector('product-form form[action*="/cart/add"]')) this.addButton.style.display = 'none';
    }

    collectLines(detail) {
      if (!this.pageSection.contains(detail.form)) return;
      const lines = this.validate() ? this.currentLines() : [];
      if (lines.length) detail.replaceMain(lines);
      else detail.cancel();
    }

    // Zonder productformulier op de pagina: zelf toevoegen.
    addDirect() {
      if (!this.validate()) return;
      const lines = this.currentLines();
      if (!lines.length) return;

      const addUrl = `${window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add'}.js`;
      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: lines }),
      })
        .then((response) => response.json())
        .then((result) => {
          if (result.status) {
            this.showStatus(fill(this.i18n.cartError, { MESSAGE: result.description || result.message || '' }), true);
            return;
          }
          this.showStatus(fill(this.i18n.cartSuccess, { COUNT: lines.length }), false);
          if (window.zawajRefreshCartIcon) window.zawajRefreshCartIcon();
        })
        .catch((error) => this.showStatus(fill(this.i18n.cartErrorGeneric, { ERROR: error }), true));
    }
  }

  customElements.define('product-bundle', ProductBundle);
}
